/**
 * Destinations — where leads are delivered: email, SMS (Phase 2, gated on a
 * registered 10DLC campaign) and signed webhooks (Standard Webhooks,
 * contracts/lead-webhook.md).
 *
 * The webhook secret (`whsec_…`) is shown exactly once — on create and on
 * rotate — in a dialog that cannot be dismissed by Esc or a stray click; the
 * API never returns it again. Rotation keeps the previous secret valid for
 * 24 h (the retry schedule's tail), which the confirm dialog says.
 */
import { useState, type FormEvent } from 'react';
import { useSession } from '../auth/session';
import { SUBSCRIBABLE_EVENTS, type Destination, type DestinationKind, type DeliveryStatus, type SubscribableEvent } from '../api/types';
import { Link, useRouter } from '../router';
import { CopyButton, Empty, ErrorNotice, Field, Loading, Modal, NotSet, PageHead, Segmented, Time, formatDateTime, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

export function SecretReveal({ secret, onDone, rotated }: { secret: string | null; onDone: () => void; rotated?: boolean }) {
  const [ack, setAck] = useState(false);
  return (
    <Modal
      open={Boolean(secret)}
      title={rotated ? 'Your new signing secret' : 'Your webhook signing secret'}
      onClose={onDone}
      dismissible={false}
      footer={
        <button type="button" className="btn btn-primary" disabled={!ack} onClick={() => { setAck(false); onDone(); }}>
          Done
        </button>
      }
    >
      <p>
        Copy it now. <strong>We will never show it again</strong>; if it is lost you can only rotate it.
        {rotated ? ' The previous secret keeps working for 24 hours, so deliveries in flight still verify.' : null}
      </p>
      <div className="secret" data-testid="whsec">{secret}</div>
      <div className="row">
        <CopyButton text={secret ?? ''} label="Copy secret" className="btn" />
      </div>
      <label className="check" htmlFor="dest-secret-ack">
        <input id="dest-secret-ack" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
        <span className="t">I have stored the secret somewhere safe</span>
      </label>
    </Modal>
  );
}

export function DestinationForm({ defaultEmail, onCreated, initialKind = 'webhook' }: { defaultEmail?: string; onCreated: (d: Destination, secret?: string) => void; initialKind?: DestinationKind }) {
  const { api } = useSession();
  const [kind, setKind] = useState<DestinationKind>(initialKind);
  const [url, setUrl] = useState('');
  const [recipients, setRecipients] = useState(defaultEmail ?? '');
  const [events, setEvents] = useState<SubscribableEvent[]>(['lead.created', 'lead.updated', 'lead.accepted', 'lead.declined', 'lead.contacted']);
  const create = useMutation(async () => {
    const list = recipients.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    const config = kind === 'webhook' ? { url: url.trim(), events } : { recipients: list, events };
    return api.createDestination(kind, config);
  });
  const fieldIssues = (path: string) => (create.error?.fields ?? []).filter((f) => f.path.startsWith(path));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const created = await create.run();
    if (created) onCreated(created, created.secret);
  };

  return (
    <form className="stack" onSubmit={submit}>
      <Segmented
        label="Destination type"
        value={kind}
        onChange={setKind}
        options={[
          { value: 'webhook', label: 'Webhook' },
          { value: 'email', label: 'Email' },
          { value: 'sms', label: 'SMS' },
        ]}
      />
      {kind === 'webhook' ? (
        <Field label="Endpoint URL" hint="Public https:// on port 443. Every delivery is signed with a secret you’ll see once." issues={fieldIssues('config.url')}>
          {(p) => <input {...p} className="input input-mono" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.example.com/finderly/leads" />}
        </Field>
      ) : (
        <Field
          label={kind === 'email' ? 'Email recipients' : 'Phone numbers'}
          hint={kind === 'sms' ? 'SMS goes live once your 10DLC campaign is registered (Phase 2).' : 'Comma separated, up to 20.'}
          issues={fieldIssues('config.recipients')}
        >
          {(p) => <input {...p} className="input" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder={kind === 'email' ? 'ops@example.com' : '+15550102000'} />}
        </Field>
      )}
      <fieldset>
        <legend className="label" style={{ marginBottom: 6 }}>
          Events
        </legend>
        <div className="chips">
          {SUBSCRIBABLE_EVENTS.map((ev) => (
            <button
              key={ev}
              type="button"
              className="chip mono"
              aria-pressed={events.includes(ev)}
              onClick={() => setEvents(events.includes(ev) ? events.filter((x) => x !== ev) : [...events, ev])}
            >
              {ev}
            </button>
          ))}
        </div>
      </fieldset>
      <ErrorNotice error={create.error} />
      <div>
        <button className="btn btn-primary" type="submit" disabled={create.pending || events.length === 0 || (kind === 'webhook' ? !url.trim() : !recipients.trim())}>
          {create.pending ? 'Creating…' : 'Add destination'}
        </button>
      </div>
    </form>
  );
}

function describe(d: Destination): string {
  if (d.kind === 'webhook') return String(d.config.url ?? '');
  return (d.config.recipients ?? []).join(', ');
}

export function DestinationList({ items, compact }: { items: Destination[]; compact?: boolean }) {
  if (!items.length) return <Empty title="No destinations yet">Add one so new leads reach someone.</Empty>;
  // Only webhooks have a signing secret: an email- or SMS-only list has no
  // "Secret rotated" column at all, and in a mixed list the other rows say so.
  const secrets = !compact && items.some((d) => d.kind === 'webhook');
  return (
    <div className="table-wrap">
      <table className="data">
        <caption className="visually-hidden">Destinations</caption>
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Target</th>
            <th scope="col">Status</th>
            {secrets ? <th scope="col">Secret rotated</th> : null}
            <th scope="col">
              <span className="visually-hidden">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id}>
              <td>
                <span className="tag">{d.kind}</span>
              </td>
              <td className="mono" style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {describe(d)}
              </td>
              <td>
                <span className={`dotstat ${d.status === 'active' ? 'ok' : ''}`}>{d.status}</span>
                {d.kind === 'sms' && d.sms_campaign_status !== 'registered' ? <span className="tag warn" style={{ marginLeft: 6 }}>10DLC pending</span> : null}
              </td>
              {secrets ? (
                <td>
                  {d.kind === 'webhook' ? (
                    d.secret_rotated_at ? <Time iso={d.secret_rotated_at} /> : <span className="faint">Never</span>
                  ) : (
                    <span className="faint small">No secret ({d.kind === 'sms' ? 'SMS' : d.kind})</span>
                  )}
                </td>
              ) : null}
              <td style={{ textAlign: 'right' }}>
                <Link className="btn btn-sm" to={`/destinations/${d.id}`}>
                  Manage
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Destinations() {
  const { api, whoami } = useSession();
  const list = useQuery(() => api.listDestinations(), []);
  const [adding, setAdding] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const toast = useToast();
  return (
    <div>
      <PageHead
        title="Destinations"
        lede="Every active destination receives every new lead. Webhooks are signed (Standard Webhooks) and retried for 24 hours."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setAdding(!adding)} aria-expanded={adding}>
            {adding ? 'Close' : 'Add destination'}
          </button>
        }
      />
      <div className="stack-lg">
        {adding ? (
          <section className="panel panel-pad">
            <DestinationForm
              defaultEmail={whoami?.email}
              onCreated={(_d, s) => {
                setAdding(false);
                toast('Destination added');
                if (s) setSecret(s);
                void list.reload();
              }}
            />
          </section>
        ) : null}
        <section className="panel">{list.loading ? <Loading /> : list.error ? <ErrorNotice error={list.error} /> : <DestinationList items={list.data?.items ?? []} />}</section>
      </div>
      <SecretReveal secret={secret} onDone={() => setSecret(null)} />
    </div>
  );
}

const STATUS_TONE: Record<string, string> = { sent: 'ok', failed: 'bad', gave_up: 'bad', pending: 'warn' };
/** UI words for the delivery statuses; the API value `gave_up` stays as it is. */
export const DELIVERY_STATUS_LABEL: Record<string, string> = { sent: 'Sent', pending: 'Pending', failed: 'Failed', gave_up: 'Failed permanently' };

export function DestinationDetail({ id }: { id: string }) {
  const { api } = useSession();
  const { navigate } = useRouter();
  const toast = useToast();
  const [status, setStatus] = useState<DeliveryStatus | 'all'>('all');
  const [secret, setSecret] = useState<string | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const list = useQuery(() => api.listDestinations(), []);
  const deliveries = useQuery(() => api.listDeliveries(id, status === 'all' ? undefined : status), [id, status], { pollMs: 8_000 });
  const dest = list.data?.items.find((d) => d.id === id) ?? null;
  const rotate = useMutation(() => api.rotateSecret(id));
  const test = useMutation(() => api.testDestination(id));
  const toggle = useMutation((next: 'active' | 'disabled') => api.updateDestination(id, { status: next }));

  if (list.loading) return <Loading />;
  const webhook = dest?.kind === 'webhook';
  if (!dest) return <Empty title="Destination not found"><Link to="/destinations">Back to destinations</Link></Empty>;

  return (
    <div>
      <PageHead
        eyebrow={<Link to="/destinations">Destinations</Link>}
        title={dest.kind === 'webhook' ? 'Webhook' : dest.kind === 'email' ? 'Email' : 'SMS'}
        lede={<span className="mono">{describe(dest)}</span>}
        actions={
          <>
            <button
              type="button"
              className="btn"
              disabled={test.pending || dest.status !== 'active'}
              onClick={async () => {
                const res = await test.run();
                if (res) {
                  toast(`Test event queued (delivery ${res.delivery_id})`);
                  void deliveries.reload();
                }
              }}
            >
              {test.pending ? 'Sending…' : 'Send test event'}
            </button>
            {dest.kind === 'webhook' ? (
              <button type="button" className="btn" onClick={() => setConfirmRotate(true)}>
                Rotate secret
              </button>
            ) : null}
            <button
              type="button"
              className={`btn ${dest.status === 'active' ? 'btn-danger' : ''}`}
              disabled={toggle.pending}
              onClick={async () => {
                const next = dest.status === 'active' ? 'disabled' : 'active';
                if (next === 'disabled' && !window.confirm('Stop sending leads to this destination?')) return;
                if (await toggle.run(next)) void list.reload();
              }}
            >
              {dest.status === 'active' ? 'Disable' : 'Enable'}
            </button>
          </>
        }
      />
      <div className="stack-lg">
        <ErrorNotice error={test.error ?? rotate.error ?? toggle.error} />
        <section className="panel panel-pad">
          <dl className="facts">
            <dt>Status</dt>
            <dd>
              <span className={`dotstat ${dest.status === 'active' ? 'ok' : ''}`}>{dest.status}</span>
            </dd>
            <dt>Events</dt>
            <dd>{dest.config.events?.length ? dest.config.events.join(', ') : 'All lead events'}</dd>
            {dest.kind === 'webhook' ? (
              <>
                <dt>Signing secret</dt>
                <dd>
                  Hidden · {dest.secret_rotated_at ? <>rotated <Time iso={dest.secret_rotated_at} /></> : 'never rotated'}
                </dd>
                <dt>Verify with</dt>
                <dd className="small">
                  Standard Webhooks headers <code className="key">webhook-id</code> <code className="key">webhook-timestamp</code>{' '}
                  <code className="key">webhook-signature</code>
                </dd>
              </>
            ) : null}
            <dt>Created</dt>
            <dd>{formatDateTime(dest.created_at)}</dd>
          </dl>
        </section>
        <section className="panel">
          <div className="panel-head">
            <h2>Delivery log</h2>
            <Segmented
              label="Filter deliveries"
              value={status}
              onChange={setStatus}
              options={[
                { value: 'all', label: 'All' },
                { value: 'sent', label: 'Sent' },
                { value: 'pending', label: 'Pending' },
                { value: 'failed', label: 'Failed' },
                { value: 'gave_up', label: DELIVERY_STATUS_LABEL.gave_up ?? 'Failed permanently' },
              ]}
            />
          </div>
          {deliveries.loading ? (
            <Loading />
          ) : deliveries.data?.items.length ? (
            <div className="table-wrap">
              <table className="data">
                <caption className="visually-hidden">Delivery log</caption>
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Event</th>
                    <th scope="col">Lead</th>
                    <th scope="col" className="num">Attempt</th>
                    <th scope="col">Status</th>
                    {/* Only a webhook answers with an HTTP status; an email row showed "HTTP —". */}
                    {webhook ? (
                      <th scope="col" className="num">
                        HTTP
                      </th>
                    ) : null}
                    <th scope="col">Event id</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.data.items.map((d) => (
                    <tr key={d.id}>
                      <td className="num">
                        <LogWhen iso={d.created_at} />
                      </td>
                      <td className="mono">{d.event_type}</td>
                      <td className="nowrap">
                        {d.lead_id ? (
                          <Link to={`/leads/${d.lead_id}`} aria-label={`Open the lead for delivery ${d.event_id}`}>
                            Open lead
                          </Link>
                        ) : (
                          <span className="small muted">{d.event_type === 'widget.test' ? 'Test event' : <NotSet />}</span>
                        )}
                      </td>
                      <td className="num">{d.attempt}</td>
                      <td>
                        <span className={`tag ${STATUS_TONE[d.status] ?? ''}`} style={{ whiteSpace: 'normal' }}>{DELIVERY_STATUS_LABEL[d.status] ?? d.status.replace('_', ' ')}</span>
                      </td>
                      {webhook ? <td className="num">{d.response_code ?? <NotSet label="No response" />}</td> : null}
                      <td className="mono small faint">{d.event_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : status !== 'all' ? (
            // The filter hides everything; it is not an empty log (third live review, 2026-09-27).
            <Empty title={`No ${(DELIVERY_STATUS_LABEL[status] ?? status).toLowerCase()} deliveries`}>
              <button type="button" className="btn" onClick={() => setStatus('all')}>
                Show all
              </button>
            </Empty>
          ) : (
            <Empty title="No deliveries yet">Send a test event to see one here.</Empty>
          )}
        </section>
      </div>
      <Modal
        open={confirmRotate}
        title="Rotate the signing secret?"
        onClose={() => setConfirmRotate(false)}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmRotate(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={rotate.pending}
              onClick={async () => {
                const res = await rotate.run();
                setConfirmRotate(false);
                if (res) {
                  setSecret(res.secret);
                  void list.reload();
                }
              }}
            >
              {rotate.pending ? 'Rotating…' : 'Rotate'}
            </button>
          </>
        }
      >
        <p>A new secret is issued and shown once. The current one keeps working for 24 hours, so you can deploy the new one without dropping deliveries.</p>
      </Modal>
      <SecretReveal secret={secret} rotated onDone={() => { setSecret(null); navigate(`/destinations/${id}`, { replace: true }); }} />
    </div>
  );
}

/**
 * The delivery log's "When": the date and the clock time are each kept
 * whole, so a narrow column breaks between them (two lines) rather than
 * between every word — at 390 px "Sep 25, 6:39 AM" wrapped to four lines.
 */
function LogWhen({ iso }: { iso: string | null }) {
  const d = iso ? new Date(iso) : null;
  if (!iso || !d || Number.isNaN(d.getTime())) return <>{formatDateTime(iso)}</>;
  return (
    <time dateTime={iso}>
      <span className="nowrap">{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })},</span>{' '}
      <span className="nowrap">{d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
    </time>
  );
}
